<template>
  <AdminLayout>
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6">
        <router-link to="/audit" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft class="w-4 h-4" /> Retour aux logs d'audit
        </router-link>
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-orange-50 rounded-xl">
            <ArchiveIcon class="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Archives d'audit (MinIO)</h1>
            <p class="text-sm text-dashboard-muted mt-0.5">
              Archives mensuelles JSONL gzippées. Rétention conseillée : 10 ans.
            </p>
          </div>
        </div>
      </div>

      <!-- Policy info -->
      <div class="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 flex items-start gap-3">
        <Info class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div class="text-sm text-blue-900">
          <p class="font-medium mb-1">Politique de sauvegarde</p>
          <ul class="text-blue-800 space-y-0.5 text-xs list-disc list-inside">
            <li>Export <strong>automatique mensuel</strong> du mois précédent (cron : 1er du mois à 04:00).</li>
            <li>Stockage <strong>MinIO</strong> dans <code class="bg-white px-1 rounded">audit-archives/YYYY/MM/</code>.</li>
            <li>Données restent en base 24 mois glissants ; archives MinIO conservées 10 ans.</li>
            <li>Restauration possible via <code class="bg-white px-1 rounded">python manage.py audit_restore</code>.</li>
          </ul>
        </div>
      </div>

      <!-- Stats + manual export -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div class="bg-white border border-dashboard-border rounded-xl p-4">
          <p class="text-xs text-dashboard-muted">Archives stockées</p>
          <p class="text-2xl font-bold text-dashboard-text">{{ archives.length }}</p>
        </div>
        <div class="bg-white border border-dashboard-border rounded-xl p-4">
          <p class="text-xs text-dashboard-muted">Volume total</p>
          <p class="text-2xl font-bold text-dashboard-text">{{ formatBytes(totalSize) }}</p>
        </div>
        <div class="bg-white border border-dashboard-border rounded-xl p-4">
          <p class="text-xs text-dashboard-muted">Dernier export</p>
          <p class="text-sm font-medium text-dashboard-text mt-1">{{ lastExportDate || '—' }}</p>
        </div>
      </div>

      <!-- Manual export -->
      <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-5">
        <h2 class="text-sm font-semibold text-dashboard-text mb-3 flex items-center gap-2">
          <Zap class="w-4 h-4 text-amber-500" />
          Déclencher un export manuel
        </h2>
        <div class="flex items-end gap-3 flex-wrap">
          <div>
            <label class="block text-xs text-dashboard-muted mb-1">Mois à exporter (YYYY-MM)</label>
            <input
              v-model="exportMonth"
              type="month"
              class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]"
            />
          </div>
          <button
            @click="triggerExport"
            :disabled="!exportMonth || exporting"
            class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Upload :class="['w-4 h-4', exporting && 'animate-pulse']" />
            {{ exporting ? 'Export en cours…' : 'Exporter maintenant' }}
          </button>
          <p class="text-xs text-dashboard-muted">
            L'export est <strong>idempotent</strong> : si l'archive existe déjà, elle sera écrasée.
          </p>
        </div>
      </div>

      <!-- Archives list -->
      <div class="bg-white border border-dashboard-border rounded-xl shadow-sm overflow-hidden">
        <div class="px-4 py-3 border-b border-dashboard-border flex items-center justify-between">
          <h2 class="text-sm font-semibold text-dashboard-text flex items-center gap-2">
            <ArchiveIcon class="w-4 h-4" />
            Archives mensuelles
          </h2>
          <button @click="fetchArchives" :disabled="loading" class="text-dashboard-muted hover:text-dashboard-text disabled:opacity-50">
            <RefreshCw :class="['w-4 h-4', loading && 'animate-spin']" />
          </button>
        </div>

        <div v-if="loading && archives.length === 0" class="p-12 text-center">
          <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-3" />
          <p class="text-dashboard-muted">Chargement…</p>
        </div>

        <div v-else-if="error" class="p-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p class="font-medium text-red-800">Erreur</p>
              <p class="text-sm text-red-600 mt-1">{{ error }}</p>
            </div>
          </div>
        </div>

        <div v-else-if="archives.length === 0" class="p-12 text-center">
          <ArchiveIcon class="w-10 h-10 text-dashboard-muted mx-auto mb-3" />
          <p class="text-dashboard-muted text-sm">Aucune archive trouvée dans MinIO.</p>
          <p class="text-xs text-dashboard-muted mt-1">Le premier export est généré le 1er du mois suivant la mise en service.</p>
        </div>

        <ul v-else class="divide-y divide-dashboard-border">
          <li
            v-for="archive in archives"
            :key="archive.path"
            class="px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-center gap-4"
          >
            <div class="p-2 bg-orange-50 rounded-lg flex-shrink-0">
              <Calendar class="w-5 h-5 text-orange-600" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-dashboard-text">{{ monthName(archive.year, archive.month) }}</p>
              <p class="text-xs text-dashboard-muted truncate font-mono">{{ archive.path }}</p>
            </div>
            <div class="text-right text-xs text-dashboard-muted hidden sm:block">
              <p>{{ formatBytes(archive.size_bytes) }}</p>
              <p v-if="archive.modified_at">{{ formatDate(archive.modified_at) }}</p>
            </div>
            <a
              :href="downloadUrl(archive.year, archive.month)"
              target="_blank"
              rel="noopener"
              class="px-3 py-1.5 bg-[#22282F] text-white rounded-lg text-xs font-medium hover:bg-[#22282F]/90 transition-colors flex items-center gap-1.5"
            >
              <Download class="w-3.5 h-3.5" />
              Télécharger
            </a>
          </li>
        </ul>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  ArrowLeft, Archive as ArchiveIcon, Info, Zap, Upload, RefreshCw, AlertCircle,
  Calendar, Download,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { formatBytes } from '@/utils/auditMeta'

interface ArchiveItem {
  year: number
  month: number
  filename: string
  path: string
  size_bytes: number | null
  modified_at: string | null
}

const toast = useToast()

const archives = ref<ArchiveItem[]>([])
const loading = ref(false)
const error = ref('')
const exporting = ref(false)
const exportMonth = ref(currentMonthMinusOne())

function currentMonthMinusOne(): string {
  const now = new Date()
  const month = now.getMonth() === 0 ? 12 : now.getMonth()
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  return `${year}-${String(month).padStart(2, '0')}`
}

const totalSize = computed(() =>
  archives.value.reduce((sum, a) => sum + (a.size_bytes || 0), 0),
)

const lastExportDate = computed(() => {
  const mostRecent = archives.value
    .filter(a => a.modified_at)
    .sort((a, b) => (b.modified_at! < a.modified_at! ? -1 : 1))[0]
  return mostRecent?.modified_at ? formatDate(mostRecent.modified_at) : null
})

function monthName(year: number, month: number): string {
  const names = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
  return `${names[month - 1]} ${year}`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function downloadUrl(year: number, month: number): string {
  return `${API_BASE_URL}/api/admin/audit/archives/${year}/${String(month).padStart(2, '0')}/download/`
}

async function fetchArchives() {
  loading.value = true
  error.value = ''
  try {
    const result = await apiFetch('/api/admin/audit/archives/')
    archives.value = result.archives || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur de chargement'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

async function triggerExport() {
  if (!exportMonth.value) return
  if (!confirm(`Lancer l'export du mois ${exportMonth.value} ?\nL'opération peut prendre quelques secondes.`)) return

  exporting.value = true
  try {
    const result = await apiFetch('/api/admin/audit/archives/export/', {
      method: 'POST',
      body: JSON.stringify({ month: exportMonth.value }),
    })
    if (result.count === 0) {
      toast.show({ type: 'info', title: 'Aucune donnée', message: result.message || `Pas d'entrées pour ${exportMonth.value}` })
    } else {
      toast.success('Export réussi', `${result.count} entrée(s) exportée(s) (${formatBytes(result.size_bytes)})`)
    }
    await fetchArchives()
  } catch (e: any) {
    toast.error('Erreur export', e?.message || 'Échec de l\'export')
  } finally {
    exporting.value = false
  }
}

onMounted(fetchArchives)
</script>
